const defaultHeaders = new Headers();

defaultHeaders.append("Content-Type", "application/json;charset=UTF-8");

const fetchOptions = (method = "GET", body = null) => {
  const options = {
    method,
    headers: defaultHeaders,
    credentials: "include",
    redirect: "follow",
  };
  if (body) {
    options.body = body;
  }
  return options;
};

export const getCotizacionesByEstados = async (baseUrl, idUsuario, estados) => {
  const response = await fetch(
    `${baseUrl}getCotizacionesByEstados?idUsuario=${idUsuario}&estados=${estados}`,
    fetchOptions()
  );
  if (!response.ok) {
    throw new Error(`Error al obtener las cotizaciones (${response.status})`);
  }
  return response.json();
};

export const getProductosByFiltro = async (
  baseUrl,
  { empresas, fechaInicio, fechaTermino, tipos }
) => {
  const response = await fetch(
    `${baseUrl}getProductosByFiltro?empresas=${empresas}&fechaInicio=${fechaInicio}&fechaTermino=${fechaTermino}&tipos=${tipos}`,
    fetchOptions()
  );
  if (!response.ok) {
    throw new Error(`Error al buscar productos (${response.status})`);
  }
  return response.json();
};

export const crearCotizacion = async (
  baseUrl,
  { idCliente, idCreador, empresaArrendadora }
) => {
  const response = await fetch(
    `${baseUrl}crearCotizacion?id_cliente=${idCliente}&id_creador=${idCreador}&empresa_arrendadora=${empresaArrendadora}&comentario=`,
    fetchOptions()
  );
  if (!response.ok) {
    let mensaje = `Error al crear la cotización (${response.status})`;
    try {
      const data = await response.json();
      if (data && data.mensaje) {
        mensaje = data.mensaje;
      }
    } catch (e) {
      // respuesta sin cuerpo JSON: se mantiene el mensaje por status
    }
    throw new Error(mensaje);
  }
  return response.json();
};

export const getDetalleCotizacionByIdCotizacion = async (baseUrl, idCotizacion) => {
  const response = await fetch(
    `${baseUrl}getDetalleCotizacionByIdCotizacion?idCotizacion=${idCotizacion}`,
    fetchOptions()
  );
  if (!response.ok) {
    throw new Error(`Error al obtener el detalle de la cotización (${response.status})`);
  }
  return response.json();
};

export const validarElementosActivosCotizacion = async (baseUrl, idCotizacion) => {
  const response = await fetch(
    `${baseUrl}validarElementosActivosCotizacion?idCotizacion=${idCotizacion}`,
    fetchOptions()
  );
  if (!response.ok) {
    throw new Error(`Error al validar productos activos de la cotización (${response.status})`);
  }
  return response.json();
};

export const getCotizacionById = async (baseUrl, idCotizacion) => {
  const response = await fetch(
    `${baseUrl}getCotizacionById?idCotizacion=${idCotizacion}`,
    fetchOptions()
  );
  if (!response.ok) {
    throw new Error(`Error al obtener la cotización (${response.status})`);
  }
  return response.json();
};

export const updateCotizacion = async (
  baseUrl,
  {
    idCotizacion,
    idCliente,
    idEstado,
    idCreador,
    desde,
    hasta,
    aviso,
    observacion,
    vencimiento,
  }
) => {
  const queryParams = new URLSearchParams({
    idCotizacion,
    idCliente,
    idEstado,
    idCreador,
    desde,
    hasta,
    aviso,
  });

  if (observacion) {
    queryParams.append("observacion", observacion);
  }
  if (vencimiento) {
    queryParams.append("vencimiento", vencimiento);
  }

  const response = await fetch(`${baseUrl}updateCotizacion?${queryParams}`, fetchOptions());
  if (!response.ok) {
    throw new Error(`Error al actualizar la cotización (${response.status})`);
  }
  return response.json();
};

export const deactivateDetalleCotizacionByIdDetalle = async (baseUrl, idDetalle) => {
  const response = await fetch(
    `${baseUrl}deactivateDetalleCotizacion?idDetalle=${idDetalle}`,
    fetchOptions("PUT")
  );
  if (!response.ok) {
    throw new Error(`Error al desactivar el detalle ${idDetalle} (${response.status})`);
  }
  return true;
};

export const deactivateDetalleCotizacion = async (baseUrl, id, idCotizacion) => {
  const queryParams = new URLSearchParams({
    id,
    idCotizacion,
  });

  const response = await fetch(
    `${baseUrl}deactivateDetalleCotizacion?${queryParams}`,
    fetchOptions("PUT")
  );
  if (!response.ok) {
    throw new Error(`Error al desactivar el detalle ${id} (${response.status})`);
  }
  return response.json();
};

export const validarDisponibilidad = async (baseUrl, productosParaValidar) => {
  const response = await fetch(`${baseUrl}validarDisponibilidad`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(productosParaValidar),
  });
  if (!response.ok) {
    throw new Error(`Error al validar disponibilidad (${response.status})`);
  }
  return response.json();
};

// Guardado centralizado: el backend desactiva los detalles reemplazados,
// inserta productos/servicios, auto-inserta los subproductos incluidos desde BD,
// normaliza el Gasto Comun (uno por producto) y actualiza la cotizacion,
// todo en una transaccion.
export const guardarDetallesCotizacion = async (baseUrl, payload) => {
  const response = await fetch(`${baseUrl}guardarDetallesCotizacion`, {
    ...fetchOptions("POST", JSON.stringify(payload)),
  });
  if (!response.ok) {
    let mensaje = `Error al guardar los detalles de la cotización (${response.status})`;
    try {
      const data = await response.json();
      if (data && data.mensaje) {
        mensaje = data.mensaje;
      }
    } catch (e) {
      // respuesta sin cuerpo JSON: se mantiene el mensaje por status
    }
    throw new Error(mensaje);
  }
  return response.json();
};

// Sincronizacion transaccional de detalles (desactivar/insertar/actualizar +
// cabecera opcional). Usada por RevisarCotizacion para guardado general,
// tramos, cuotas y borrado de lineas.
export const sincronizarDetallesCotizacion = async (baseUrl, payload) => {
  const response = await fetch(`${baseUrl}sincronizarDetallesCotizacion`, {
    ...fetchOptions("POST", JSON.stringify(payload)),
  });
  if (!response.ok) {
    let mensaje = `Error al sincronizar los detalles de la cotización (${response.status})`;
    try {
      const data = await response.json();
      if (data && data.mensaje) {
        mensaje = data.mensaje;
      }
    } catch (e) {
      // respuesta sin cuerpo JSON: se mantiene el mensaje por status
    }
    throw new Error(mensaje);
  }
  return response.json();
};

// Cierre transaccional de la cotización (COTIZACION -> ASIGNACION): el backend
// aplica en UNA sola transacción la sincronización de detalles, subproductos,
// upsert de contrato, reconstrucción de períodos de facturación, cambio de
// estado a ASIGNACIÓN (5) con días de aviso y plazo de firma. Si algo falla
// hace rollback total y responde { mensaje, etapa }. Si el cierre ya estaba
// hecho (estado ASIGNACIÓN y períodos existentes) responde ok (reintento seguro).
// Respuesta exitosa: { ok, contrato: { id, idEmpresa }, ... }.
export const cerrarContratoTx = async (baseUrl, payload) => {
  const response = await fetch(`${baseUrl}cerrarContrato`, {
    ...fetchOptions("POST", JSON.stringify(payload)),
  });
  if (!response.ok) {
    let mensaje = `Error al cerrar el contrato (${response.status})`;
    let etapa = null;
    try {
      const data = await response.json();
      if (data && data.mensaje) {
        mensaje = data.mensaje;
      }
      if (data && data.etapa) {
        etapa = data.etapa;
      }
    } catch (e) {
      // respuesta sin cuerpo JSON: se mantiene el mensaje por status
    }
    const error = new Error(mensaje);
    if (etapa) {
      error.etapa = etapa;
    }
    throw error;
  }
  return response.json();
};

export const insertDetalleCotizacion = async (baseUrl, params) => {
  const handleInfinity = (value) =>
    value === Infinity || value === -Infinity ? 0 : value;

  const queryParams = Object.keys(params)
    .map(
      (key) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(
          key === "descuento" ? handleInfinity(params[key]) : params[key]
        )}`
    )
    .join("&");

  const response = await fetch(
    `${baseUrl}insertDetalleCotizacion?${queryParams}`,
    fetchOptions()
  );
  if (!response.ok) {
    throw new Error(`Error al insertar detalle de cotización (${response.status})`);
  }
  return response.json();
};

export const getServiciosByIdEmpresa = async (baseUrl, idEmpresa) => {
  const response = await fetch(
    `${baseUrl}getServiciosByIdEmpresa?idEmpresa=${idEmpresa}`,
    fetchOptions()
  );
  if (!response.ok) {
    throw new Error(`Error al obtener los servicios (${response.status})`);
  }
  return response.json();
};

export const getAllSubproductosByIdProducto = async (baseUrl, idProducto) => {
  const response = await fetch(
    `${baseUrl}getAllSubproductosByIdProducto?idProducto=${idProducto}`,
    fetchOptions()
  );
  if (!response.ok) {
    throw new Error(`Error al obtener los subproductos del producto (${response.status})`);
  }
  return response.json();
};

export const getAllMonedas = async (baseUrl) => {
  const response = await fetch(`${baseUrl}getAllMonedas`, fetchOptions());
  if (!response.ok) {
    throw new Error(`Error al obtener las monedas (${response.status})`);
  }
  return response.json();
};

export const updateDetalleCotizacion = async (baseUrl, params) => {
  const queryParams = Object.keys(params)
    .map(
      (key) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`
    )
    .join("&");

  const response = await fetch(
    `${baseUrl}updateDetalleCotizacion?${queryParams}`,
    fetchOptions()
  );
  if (!response.ok) {
    throw new Error(`Error al actualizar el detalle de cotización (${response.status})`);
  }
  return response.json();
};

export const actualizarDetalleCotizacion = async (baseUrl, params) => {
  const queryParams = Object.keys(params)
    .map(
      (key) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`
    )
    .join("&");

  const response = await fetch(
    `${baseUrl}actualizarDetalleCotizacion?${queryParams}`,
    fetchOptions()
  );
  if (!response.ok) {
    throw new Error(`Error al actualizar el detalle de cotización (${response.status})`);
  }
  return response.json();
};

export const recotizar = async (baseUrl, idCotizacion, idCreador) => {
  const response = await fetch(
    `${baseUrl}recotizar?idCotizacion=${idCotizacion}&idCreador=${idCreador}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
  if (!response.ok) {
    let mensaje = `Error al recotizar (${response.status})`;
    try {
      const data = await response.json();
      if (data && data.mensaje) {
        mensaje = data.mensaje;
      }
    } catch (e) {
      // respuesta sin cuerpo JSON: se mantiene el mensaje por status
    }
    throw new Error(mensaje);
  }
  return response.json();
};

export const getInformacionFaltanteByIdCliente = async (baseUrl, idCliente) => {
  const response = await fetch(
    `${baseUrl}getInformacionFaltanteByIdCliente?idCliente=${idCliente}`,
    fetchOptions()
  );
  if (!response.ok) {
    throw new Error(`Error al obtener información faltante del cliente (${response.status})`);
  }
  return response.json();
};

export const insertProductoSubproducto = async (
  baseUrl,
  { idEmpresa, idProducto, idTipoSubproducto, metraje }
) => {
  const response = await fetch(
    `${baseUrl}insertProductoSubproducto?idEmpresa=${idEmpresa}&idProducto=${idProducto}&idTipoSubproducto=${idTipoSubproducto}&metraje=${metraje}`,
    fetchOptions("POST")
  );
  if (!response.ok) {
    throw new Error(`Error al insertar subproducto del producto (${response.status})`);
  }
  return true;
};

export const renovarAsignacion = async (
  baseUrl,
  { idCotizacion, idCreador, fechaHastaRenovacion, observacion }
) => {
  const response = await fetch(
    `${baseUrl}renovar?idCotizacion=${idCotizacion}&idCreador=${idCreador}&fechaHastaRenovacion=${fechaHastaRenovacion}&observacion=${observacion}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    }
  );
  if (!response.ok) {
    throw new Error(`Error al renovar la asignación (${response.status})`);
  }
  return response.json();
};

export const noRenovarAsignacion = async (
  baseUrl,
  { idCotizacion, idCreador, observacion }
) => {
  const response = await fetch(
    `${baseUrl}noRenovar?idCotizacion=${idCotizacion}&idCreador=${idCreador}&observacion=${encodeURIComponent(observacion)}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
    }
  );
  if (!response.ok) {
    throw new Error("No se pudo marcar como no renovada");
  }
  return true;
};

export const getAllMonedasByMonthYear = async (baseUrl, mes, anio) => {
  const response = await fetch(
    `${baseUrl}getAllMonedasByMonthYear?mes=${parseInt(mes)}&anio=${anio}`,
    fetchOptions()
  );
  if (!response.ok) {
    throw new Error("Failed to fetch UF data");
  }
  return response.json();
};

export const getMetadataByCotizacionId = async (baseUrl, idCotizacion) => {
  const response = await fetch(
    `${baseUrl}getMetadataByCotizacionId?idCotizacion=${encodeURIComponent(idCotizacion)}`,
    fetchOptions()
  );
  if (!response.ok) {
    throw new Error(`No se pudo obtener metadata de cotización (${response.status})`);
  }
  return response.json();
};
